import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ChevronRight, ChevronLeft, Loader2, Compass } from "lucide-react";
import { api } from "@/lib/api";

const PROFESSIONS = [
  { id: "csf_advisor",         fr: "Conseiller en sécurité financière" },
  { id: "mortgage_broker",     fr: "Courtier hypothécaire" },
  { id: "cpa",                 fr: "Comptable / CPA" },
  { id: "real_estate",         fr: "Courtier immobilier" },
  { id: "notary",              fr: "Notaire" },
  { id: "immigration_advisor", fr: "Conseiller en immigration" },
];

const PIVOT_LABELS = {
  communication:        "Communication (écoute · clarté · questions)",
  prospecting:          "Prospection (démarchage · appels · e-mails)",
  strategic_networking: "Réseautage (événements · partenaires · centres d'influence)",
  execution:            "Exécution (terminer ce qu'on commence)",
  discipline:           "Discipline (rituels · constance)",
};

export default function AbiOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [goalOptions, setGoalOptions] = useState([]);
  const [apkeCards, setApkeCards] = useState([]);

  const [answers, setAnswers] = useState({
    profession:         "",
    territory:          { city: "", province: "QC" },
    north_star_key:     "",
    north_star_target:  0,
    weekly_dev_minutes: 60,
    skill_scores:       {},
  });

  useEffect(() => {
    api.get("/abi/copilot/onboarding/status").then(r => {
      if (r.data.completed) { nav("/app/abi/passport", { replace: true }); return; }
      setGoalOptions(r.data.goal_options || []);
      setReady(true);
    }).catch(() => setReady(true));
  }, [nav]);

  // Load 3 APKE cards for the selected profession as soon as we know it
  useEffect(() => {
    if (!answers.profession) return;
    api.get(`/abi/copilot/knowledge?profession_id=${answers.profession}`)
      .then(r => setApkeCards((r.data.cards || []).slice(0, 3)))
      .catch(() => {});
  }, [answers.profession]);

  const submit = async () => {
    if (!answers.profession) return toast.error("Sélectionnez votre profession.");
    if (!answers.north_star_key) return toast.error("Choisissez votre objectif principal.");
    setSaving(true);
    try {
      await api.post("/abi/copilot/onboarding/complete", answers);
      toast.success("Votre parcours vient de commencer !");
      nav("/app/abi/passport", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  if (!ready) return (
    <div className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: "#FBF6EA" }}>
      <Loader2 size={20} className="animate-spin" />
    </div>
  );

  const STEPS = [
    { title: "Bienvenue dans ALTIORA",
      subtitle: "Un coach IA qui va vous aider à devenir un meilleur professionnel — semaine après semaine.",
      body: (
        <div className="space-y-4 text-[15px] leading-relaxed text-slate-800">
          <p>Chaque semaine, ALTIORA va :</p>
          <ul className="space-y-1 ml-4">
            <li>· <b>observer</b> votre activité réelle</li>
            <li>· <b>mesurer</b> vos compétences</li>
            <li>· <b>identifier</b> vos leviers de progression</li>
            <li>· <b>construire</b> votre plan de développement</li>
            <li>· <b>démontrer</b> comment cela transforme votre trajectoire</li>
          </ul>
          <p className="italic text-slate-600">
            5 questions suffisent pour démarrer. Cela prend moins de 3 minutes.
          </p>
        </div>
      )
    },
    { title: "1. Votre profession",
      subtitle: "ALTIORA adaptera son coaching à votre métier.",
      body: (
        <div className="grid md:grid-cols-2 gap-2">
          {PROFESSIONS.map(p => (
            <button key={p.id}
                    onClick={() => setAnswers(a => ({ ...a, profession: p.id }))}
                    data-testid={`onb-prof-${p.id}`}
                    className="text-left p-3 rounded border transition"
                    style={{
                      borderColor: answers.profession === p.id ? "#0B2545" : "#E5E7EB",
                      backgroundColor: answers.profession === p.id ? "#EEF4FF" : "white",
                      color: "#0B2545",
                    }}>
              <span className="font-serif text-[15px]">{p.fr}</span>
            </button>
          ))}
        </div>
      )
    },
    { title: "2. Votre territoire",
      subtitle: "Le Radar priorisera les opportunités locales.",
      body: (
        <div className="space-y-3">
          <div>
            <label className="text-[13px] uppercase tracking-widest font-bold text-slate-600">Ville</label>
            <input type="text" value={answers.territory.city}
                    onChange={e => setAnswers(a => ({ ...a,
                      territory: { ...a.territory, city: e.target.value } }))}
                    placeholder="ex. Lévis"
                    className="w-full mt-1 border rounded px-3 py-2 text-[14px]"
                    data-testid="onb-territory-city" />
          </div>
          <div>
            <label className="text-[13px] uppercase tracking-widest font-bold text-slate-600">Province</label>
            <select value={answers.territory.province}
                     onChange={e => setAnswers(a => ({ ...a,
                       territory: { ...a.territory, province: e.target.value } }))}
                     className="w-full mt-1 border rounded px-3 py-2 text-[14px]"
                     data-testid="onb-territory-province">
              <option value="QC">Québec</option>
              <option value="ON">Ontario</option>
              <option value="NB">Nouveau-Brunswick</option>
              <option value="NS">Nouvelle-Écosse</option>
            </select>
          </div>
        </div>
      )
    },
    { title: "3. Votre objectif principal",
      subtitle: "Le North Star qui alignera vos actions quotidiennes.",
      body: (
        <div className="space-y-3">
          <select value={answers.north_star_key}
                   onChange={e => setAnswers(a => ({ ...a, north_star_key: e.target.value }))}
                   className="w-full border rounded px-3 py-2 text-[14px]"
                   data-testid="onb-northstar-goal">
            <option value="">— Choisir un objectif —</option>
            {goalOptions.map(g => (
              <option key={g.key} value={g.key}>
                {g.label_fr} ({g.frequency === "yearly" ? "an" : g.frequency === "monthly" ? "mois" : "semaine"})
              </option>
            ))}
          </select>
          {answers.north_star_key && (
            <div>
              <label className="text-[13px] uppercase tracking-widest font-bold text-slate-600">
                Cible chiffrée
              </label>
              <input type="number" value={answers.north_star_target}
                      onChange={e => setAnswers(a => ({ ...a,
                        north_star_target: parseFloat(e.target.value) || 0 }))}
                      className="w-full mt-1 border rounded px-3 py-2 text-[14px]"
                      data-testid="onb-northstar-target" />
            </div>
          )}
        </div>
      )
    },
    { title: "4. Votre engagement hebdo",
      subtitle: "Combien de minutes réservez-vous par semaine à votre développement professionnel ?",
      body: (
        <div className="space-y-3">
          <input type="range" min="0" max="600" step="30"
                  value={answers.weekly_dev_minutes}
                  onChange={e => setAnswers(a => ({ ...a,
                    weekly_dev_minutes: parseInt(e.target.value) }))}
                  className="w-full"
                  data-testid="onb-weekly-minutes" />
          <div className="text-center font-serif text-3xl" style={{ color: "#0B2545" }}>
            {answers.weekly_dev_minutes} min
          </div>
          <p className="text-[13px] text-slate-600 italic text-center">
            {answers.weekly_dev_minutes < 60 ? "Débutez avec des blocs courts mais réguliers." :
             answers.weekly_dev_minutes < 180 ? "Rythme sain pour un professionnel actif." :
             "Engagement fort — vous progresserez vite."}
          </p>
        </div>
      )
    },
    { title: "5. Auto-évaluation flash",
      subtitle: "Notez sincèrement chacune de ces 5 compétences pivots (1 · à travailler → 5 · maîtrisée).",
      body: (
        <div className="space-y-3">
          {Object.entries(PIVOT_LABELS).map(([fid, label]) => (
            <div key={fid} className="p-3 rounded border" style={{ borderColor: "#E5E7EB" }}>
              <div className="text-[13px] mb-2 text-slate-800">{label}</div>
              <div className="flex gap-2">
                {[1,2,3,4,5].map(v => (
                  <button key={v}
                          onClick={() => setAnswers(a => ({ ...a,
                            skill_scores: { ...a.skill_scores, [fid]: v } }))}
                          data-testid={`onb-skill-${fid}-${v}`}
                          className="flex-1 py-2 rounded text-[13px] font-mono transition"
                          style={{
                            backgroundColor: answers.skill_scores[fid] === v ? "#0B2545" : "#F3F4F6",
                            color: answers.skill_scores[fid] === v ? "#C5A880" : "#5B5B62",
                          }}>{v}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )
    },
    { title: "6. Le fondement scientifique",
      subtitle: "Voici 3 fiches APKE que le Copilote appliquera à votre métier — chaque conseil sera adossé à ce corpus.",
      body: (
        <div className="space-y-3" data-testid="onb-apke-tour">
          {apkeCards.length === 0 && (
            <div className="text-[13px] italic text-slate-500">
              Sélectionnez une profession à l'étape 1 pour voir les fiches.
            </div>
          )}
          {apkeCards.map(c => {
            const color = c.evidence_level === "A" ? "#0B7A3B"
                         : c.evidence_level === "B" ? "#7A5B14"
                         : c.evidence_level === "C" ? "#B45309" : "#7C3AED";
            return (
              <div key={c.card_id} className="p-3 rounded border-l-4"
                    style={{ borderLeftColor: color, backgroundColor: "#FBF6EA" }}
                    data-testid={`onb-apke-card-${c.card_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-widest font-bold"
                          style={{ color: "#7A5B14" }}>
                    {c.layer === "profession" ? "Métier" : c.layer === "universal" ? "Universelle" : "Doctrine"}
                  </span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded"
                          style={{ backgroundColor: color, color: "#fff" }}>
                    Preuve {c.evidence_level}
                  </span>
                </div>
                <div className="font-serif text-[15px]" style={{ color: "#0B2545" }}>
                  {c.title_fr}
                </div>
                <div className="text-[12px] text-slate-700 mt-1">
                  {c.principle_fr}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 italic">
                  📖 {(c.references_fr || []).slice(0, 1).join("")}
                </div>
              </div>
            );
          })}
          <div className="text-[12px] text-slate-600 italic mt-2">
            La Bibliothèque APKE contient 42+ fiches. Vous pouvez la consulter à tout moment depuis le menu.
          </div>
        </div>
      )
    },
  ];

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
          style={{ background: "linear-gradient(135deg,#0B2545 0%,#123B6E 60%,#1D5AA5 100%)" }}
          data-testid="abi-onboarding-root">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Progress */}
        <div className="h-1.5 bg-slate-100">
          <div className="h-full transition-all"
                style={{ width: `${((step+1)/STEPS.length)*100}%`,
                          background: "linear-gradient(90deg,#C5A880 0%,#F5D687 100%)" }} />
        </div>
        <div className="p-8">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold mb-1"
                style={{ color: "#7A5B14" }}>
            <Sparkles size={14} />
            Étape {step+1} / {STEPS.length}
          </div>
          <h1 className="font-serif text-2xl md:text-3xl" style={{ color: "#0B2545" }}
              data-testid="abi-onboarding-title">
            {current.title}
          </h1>
          {current.subtitle && (
            <p className="text-slate-600 mt-1 text-[14px]">{current.subtitle}</p>
          )}
          <div className="mt-6">{current.body}</div>
          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => setStep(s => Math.max(0, s-1))}
                    disabled={step === 0}
                    className="flex items-center gap-1 text-[13px] text-slate-500 hover:text-slate-800 disabled:opacity-30"
                    data-testid="abi-onboarding-back">
              <ChevronLeft size={14} /> Retour
            </button>
            {!isLast ? (
              <button onClick={() => setStep(s => s+1)}
                      className="px-6 py-2 rounded font-semibold flex items-center gap-1"
                      style={{ backgroundColor: "#0B2545", color: "#fff" }}
                      data-testid="abi-onboarding-next">
                Suivant <ChevronRight size={14} />
              </button>
            ) : (
              <button onClick={submit} disabled={saving}
                      className="px-6 py-2 rounded font-semibold flex items-center gap-2"
                      style={{ backgroundColor: "#C5A880", color: "#0B2545" }}
                      data-testid="abi-onboarding-submit">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Compass size={14} />}
                Créer mon Passeport
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
